import React, { useEffect, useRef, useState } from 'react';
import { Heading, Rule, VerticalSpace } from '@arc-ui/components';
import { useInstantSearch, useMenu } from 'react-instantsearch';
import { useAccessibility, useDeviceType } from '../../../hooks';
import { ElsewhereSection } from '../../molecules/ElsewhereSection/ElseWhereSection';
import { FacetItem } from '../../molecules/FacetItem/FacetItem';
import { ListDisclosure } from '../../atoms';
import { ConsumerLookup } from '../../molecules';
import { trackSearchAnalytics } from '../../../utils/datalayer-events';
import { FacetBucket, FacetRow, Props } from '../../../types';

const SearchFilters: React.FC<Props> = ({
  attribute,
  facetBuckets,
  nbHits,
}) => {
  const { isMobile, isTablet } = useDeviceType();
  const { refine } = useMenu({ attribute });
  const { status, indexUiState } = useInstantSearch();
  const { announce } = useAccessibility();
  const [activeFacet, setActiveFacet] = useState<string>('All results');
  const [initialBuckets, setInitialBuckets] = useState<FacetBucket[]>([]);
  const [initialHits, setInitialHits] = useState<number>(0);
  const [pendingFacet, setPendingFacet] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const trimmedQuery = (indexUiState?.query || '').trim();

  const lastTrackedFilter = useRef<{
    trimmedQuery: string;
    label: string;
  } | null>(null);

  const refinedValue = facetBuckets.some((b) => b.isRefined);
  const isSmallDevice = isMobile || isTablet;

  useEffect(() => {
    if (facetBuckets.length && !refinedValue) {
// eslint-disable-next-line react-hooks/exhaustive-deps
      setInitialBuckets(facetBuckets);
      setInitialHits(nbHits);
    }
  }, [facetBuckets, nbHits, refinedValue]);

  useEffect(() => {
    if (!refinedValue) {
      setActiveFacet('All results')
    }
  }, [refinedValue]);

  const activeValues = new Set(
    facetBuckets.filter((b) => b.isRefined).map((b) => b.value),
  );

  const facets: FacetRow[] = [
    {
      label: 'All results',
      value: '',
      count: initialHits || nbHits,
      isActive: activeValues.size === 0,
    },
    ...initialBuckets.map((b) => ({
      label: b.label,
      value: b.value,
      count: b.count,
      isActive: activeValues.has(b.value),
    })),
  ];

  const handleFacetClick = (value: string, label: string) => {
    refine(value);
    setActiveFacet(label);
    announce(`Filter applied: ${label}`);
    setPendingFacet({ value, label });
  };

  useEffect(() => {
    if (!pendingFacet || status !== 'idle') return;

    const alreadyTracked =
      lastTrackedFilter.current?.trimmedQuery === trimmedQuery &&
      lastTrackedFilter.current?.label === pendingFacet.label;

    if (!alreadyTracked) {
      trackSearchAnalytics(
        'search result page load',
        trimmedQuery,
        nbHits,
        pendingFacet.label,
      );
      lastTrackedFilter.current = {
        trimmedQuery,
        label: pendingFacet.label,
      };
    }

    setPendingFacet(null)
  }, [pendingFacet, status, trimmedQuery, nbHits]);

  const handleKey = (e: React.KeyboardEvent, value: string, label: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleFacetClick(value, label);
    }
  };

  const renderFacetItems = () => (
    <fieldset className="ee-search-facetList">
      <legend className="sr-only">Filter options</legend>
      {facets.map((f) => (
        <React.Fragment key={`facet-${f.value || 'all'}`}>
          <FacetItem
            label={f.label}
            count={f.count}
            isActive={f.isActive}
            onClick={() => handleFacetClick(f.value, f.label)}
            onKeyDown={(e) => handleKey(e, f.value, f.label)}
          />
          <Rule />
        </React.Fragment>
      ))}
    </fieldset>
  );

  return (
    <aside className="ee-search-filters" aria-label="Search filters">
      {isSmallDevice ? (
        <ListDisclosure
          summary={activeFacet}
          isMobile={isMobile}
          isTablet={isTablet}
        >
          {renderFacetItems()}
          <ConsumerLookup
            query={trimmedQuery}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        </ListDisclosure>
      ) : (
        <>
          <Heading level="2" size="s">
            Refine by:
          </Heading>
          <VerticalSpace size="12" />
          {renderFacetItems()}
          <ElsewhereSection
            query={trimmedQuery}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        </>
      )}
    </aside>
  );
};

export default SearchFilters;
